import {
  Autocomplete,
  CircularProgress,
  ListItemButton,
  ListItemText,
  TextField,
} from "@mui/material";
import { RandomCode } from "hooks";
import useAutoHighlightReason from "hooks/useAutoHighlightReason";
import { resolveEnterPick } from "utils/searchEnter";
import React from "react";
import { useTranslation } from "react-i18next";

const InputSearch = ({
  loading,
  value,
  handleInpChg,
  handleSelect,
  options,
  inputRef,
  size = "small",
  error,
  // Pulled out of `other` on purpose: the rest props land on the Autocomplete, so
  // without this the TextField's placeholder cannot be reached. Defaults to the
  // scan-oriented wording every existing caller relies on; pickers for things that
  // are never scanned (services) pass their own.
  placeholder,
  ...other
}) => {
  const { t } = useTranslation();
  const highlight = useAutoHighlightReason();

  // The first match is highlighted as results arrive, so Enter takes it. Callers keep
  // their contract: a row when one is meant, the typed TEXT when Enter should scan it.
  // An exact code beats the highlighted row, and a scanned-looking code with no exact
  // row is handed back as text (utils/searchEnter).
  const onChange = (e, picked) => {
    if (e?.key !== "Enter") return handleSelect(e, picked);
    const row = resolveEnterPick({
      typed: value,
      picked,
      reason: highlight.reasonRef.current,
      options,
    });
    return handleSelect(e, row || (typeof picked === "string" ? picked : value));
  };

  return (
    <Autocomplete
      freeSolo
      autoHighlight
      onHighlightChange={highlight.onHighlightChange}
      value={null}
      loading={loading}
      inputValue={value}
      onInputChange={handleInpChg}
      onChange={onChange}
      options={options}
      getOptionLabel={(opt) => {
        if (typeof opt === "string" || opt.inputValue) return "";
        return `${opt.label}${
          opt.subtitle && opt.subtitle.trim() !== "" ? ` (${opt.subtitle})` : ""
        }`;
      }}
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      renderOption={(prop, opt, i) => (
        <ListItemButton
          {...prop}
          key={opt.id + i + RandomCode(5)}
          sx={{
            p: 0,
            mb: "2px",
            minHeight: "auto",
            // The active row (the first match, or where the arrows went) reads as hovered.
            "&:hover, &.Mui-focused": {
              backgroundColor: "#f5f5f5 !important",
            },
          }}
        >
          <ListItemText
            primaryTypographyProps={{ fontSize: "14px", color: "#333" }}
            secondaryTypographyProps={{ fontSize: "12px", color: "#777" }}
            sx={{ m: 0 }}
            primary={opt.label}
            secondary={opt.subtitle}
          />
        </ListItemButton>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          placeholder={placeholder || t("startTypingOrScanning")}
          size={size}
          error={!!error}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      {...other}
    />
  );
};

export default InputSearch;
